/**
 * RequestDetailPage Component
 * Detail view for requesting side transfer bond
 * Uses OathUI components for consistent styling
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeftIcon,
  PencilIcon,
  XCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  UserIcon,
  InformationCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { getTransferBondDetails, cancelTransferBondRequest, selectQuote, declineQuote } from '../../../services/oathTransferBondService';
import usePermissions from '../../../hooks/usePermissions';
import EditRequestModal from './EditRequestModal';
import AcceptQuoteModal from './requesting/AcceptQuoteModal';
import DeclineQuoteModal from './requesting/DeclineQuoteModal';
import QuoteSortControls, { sortQuotes } from './requesting/QuoteSortControls';
import RequestPowersSection from './requesting/RequestPowersSection';
import RequestDocumentsSection from './requesting/RequestDocumentsSection';
import ExtendedSummaryFields from './requesting/ExtendedSummaryFields';
import { formatCurrency, formatDate, formatDateTime, formatTimeRemaining, calculateTotalFees, formatPhoneNumber, formatSSN } from './shared/formatUtils';
import {
  OathPageWrapper,
  OathSectionCard,
  OathDetailField,
  OathModal,
  OathButton,
  OathAlert,
} from '../../shared/OathUI';

// Loading skeleton
const LoadingSkeleton = () => (
  <div className="animate-pulse space-y-4">
    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i}>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20 mb-2" />
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-32" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Status badge component
const StatusBadge = ({ status, hasBeenPosted, isDeferred, executingAgentID }) => {
  let label = 'Open';
  let colorClass = 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';

  if (isDeferred === 'Y') {
    label = 'Cancelled';
    colorClass = 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  } else if (status === 'C') {
    label = 'Closed';
    colorClass = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  } else if (hasBeenPosted) {
    label = 'Verification';
    colorClass = 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
  } else if (executingAgentID) {
    label = 'Agent Posting';
    colorClass = 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
      {label}
    </span>
  );
};

// Use centralized OathDetailField and OathSectionCard from OathUI

// Quote card component
const QuoteCard = ({ quote, isAccepted, onView, onAccept, onDecline, canViewPostingAgency, countdown }) => {
  const totalFees = calculateTotalFees(quote);
  const isExpired = countdown === 'Expired';
  const postingHours = quote.promisedPostingTimeInMinutes ? (quote.promisedPostingTimeInMinutes / 60).toFixed(1) : '-';

  return (
    <div className={`border rounded-lg p-4 ${isAccepted ? 'border-green-500 bg-green-50 dark:bg-green-900/20 dark:border-green-700' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          {canViewPostingAgency && quote.dbaAgencyName && (
            <p className="text-sm font-medium text-gray-900 dark:text-white">{quote.dbaAgencyName}</p>
          )}
          <p className="text-lg font-bold text-gray-900 dark:text-white">{formatCurrency(totalFees)}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Total Fees</p>
        </div>
        {isAccepted ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 rounded text-xs font-medium">
            <CheckCircleIcon className="h-4 w-4" /> Accepted
          </span>
        ) : (
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
            isExpired ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
          }`}>
            <ClockIcon className="h-4 w-4" />
            {countdown}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm mb-3">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Execution</p>
          <p className="font-medium dark:text-white">{formatCurrency(quote.executionFee)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Travel</p>
          <p className="font-medium dark:text-white">{formatCurrency(quote.travelFee)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Post Time</p>
          <p className="font-medium dark:text-white">{postingHours}h</p>
        </div>
      </div>

      {quote.feeDescription && (
        <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
          <span className="font-medium">Fee Note:</span> {quote.feeDescription}
        </p>
      )}

      {quote.postingStipulations && (
        <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
          <span className="font-medium">Stipulations:</span> {quote.postingStipulations}
        </p>
      )}

      {quote.paymentRequiredUpFront && (
        <div className="flex items-center gap-1 text-xs text-warning bg-warning/10 px-2 py-1 rounded mb-3">
          <ExclamationTriangleIcon className="h-4 w-4" />
          Payment required upfront
        </div>
      )}

      {!isAccepted && !isExpired && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => onAccept(quote)}
            className="flex-1 py-2 px-3 bg-primary text-white text-sm font-medium rounded hover:bg-primary-dark transition-colors"
          >
            Accept Quote
          </button>
          <button
            onClick={() => onDecline(quote)}
            className="py-2 px-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Decline
          </button>
        </div>
      )}
    </div>
  );
};

// Cancel Modal
const CancelModal = ({ isOpen, onClose, onConfirm, isSubmitting }) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = () => {
    if (!reason.trim()) {
      setError('Please provide a reason for cancellation.');
      return;
    }
    setError('');
    onConfirm(reason);
  };

  const handleClose = () => {
    setReason('');
    setError('');
    onClose();
  };

  return (
    <OathModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Cancel Request"
      size="md"
      footer={
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
          <OathButton
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            Keep Request
          </OathButton>
          <OathButton
            variant="danger"
            onClick={handleConfirm}
            loading={isSubmitting}
            className="w-full sm:w-auto"
          >
            {isSubmitting ? 'Cancelling...' : 'Cancel Request'}
          </OathButton>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Please provide a brief reason for cancellation. This action cannot be undone.
        </p>
        {error && (
          <OathAlert variant="error" message={error} />
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for cancellation"
            maxLength={80}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
            rows={3}
          />
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{reason.length}/80</p>
        </div>
      </div>
    </OathModal>
  );
};

// AcceptQuoteModal, DeclineQuoteModal, QuoteSortControls, RequestPowersSection,
// ExtendedSummaryFields are imported from ./requesting/

const RequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { can } = usePermissions();
  const canViewPostingAgency = can('READ:TRANSFER-BOND-POSTING-AGENCY');
  const canViewRequestingAgency = can('READ:TRANSFER-BOND-REQUESTING-AGENCY');

  const [bondDetails, setBondDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdowns, setCountdowns] = useState({});

  // Modal states
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [harrisDialogOpen, setHarrisDialogOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Quote sorting
  const [sortBy, setSortBy] = useState('none');

  // Fetch bond details
  const fetchDetails = useCallback(async () => {
    if (!id) return;

    try {
      const response = await getTransferBondDetails(id);
      if (response.success) {
        setBondDetails(response.data);
        setError(null);
      } else {
        setError(response.error || 'Failed to load details');
      }
    } catch (err) {
      setError('Failed to load details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Initial fetch and polling — pause while any input modal is open
  useEffect(() => {
    fetchDetails();

    // Don't poll while user is filling out a form (matches legacy behavior)
    if (acceptModalOpen || declineModalOpen || cancelModalOpen || editModalOpen) return;

    const interval = setInterval(fetchDetails, 10000);
    return () => clearInterval(interval);
  }, [fetchDetails, acceptModalOpen, declineModalOpen, cancelModalOpen, editModalOpen]);

  // Update countdowns every second
  useEffect(() => {
    const quotes = bondDetails?.quotes || [];
    if (quotes.length === 0) return;

    const interval = setInterval(() => {
      const newCountdowns = {};
      quotes.forEach((quote) => {
        newCountdowns[quote.transferBondQuoteID] = formatTimeRemaining(quote.expireDate);
      });
      setCountdowns(newCountdowns);
    }, 1000);

    return () => clearInterval(interval);
  }, [bondDetails?.quotes]);

  // Check if action param is present
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'cancel') {
      setCancelModalOpen(true);
    }
  }, [searchParams]);

  // Handle cancel request
  const handleCancelRequest = async (reason) => {
    setIsSubmitting(true);
    try {
      const response = await cancelTransferBondRequest(id, reason);
      if (response.success) {
        toast.success('Request canceled successfully.');
        navigate('/app/transfer-bonds?tab=requesting');
      } else {
        toast.error(response.error || 'Failed to cancel request.');
        setError(response.error || 'Failed to cancel request');
      }
    } catch (err) {
      toast.error('Failed to cancel request.');
      setError('Failed to cancel request');
    } finally {
      setIsSubmitting(false);
      setCancelModalOpen(false);
    }
  };

  // Handle accept quote - receives full payload from AcceptQuoteModal
  const handleAcceptQuote = async (payload) => {
    setIsSubmitting(true);
    try {
      const response = await selectQuote(id, {
        transferBondQuoteID: payload.quoteId,
        ...payload,
      });
      if (response.success) {
        toast.success('Quote accepted successfully.');
        fetchDetails();
        setAcceptModalOpen(false);
        setSelectedQuote(null);
      } else {
        toast.error(response.error || 'Failed to accept quote.');
        setError(response.error || 'Failed to accept quote');
      }
    } catch (err) {
      toast.error('Failed to accept quote.');
      setError('Failed to accept quote');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle decline quote - called from DeclineQuoteModal with reason
  const handleDeclineQuote = async (reason) => {
    if (!selectedQuote) return;
    setIsSubmitting(true);
    try {
      const response = await declineQuote(id, {
        transferBondQuoteID: selectedQuote.transferBondQuoteID,
        reason,
      });
      if (response.success) {
        toast.success('Quote declined successfully.');
        fetchDetails();
        setDeclineModalOpen(false);
        setSelectedQuote(null);
      } else {
        toast.error(response.error || 'Failed to decline quote.');
        setError(response.error || 'Failed to decline quote');
      }
    } catch (err) {
      toast.error('Failed to decline quote.');
      setError('Failed to decline quote');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open accept modal
  const openAcceptModal = (quote) => {
    // Harris County, TX requests cannot be finalized through the portal —
    // surface the contact-Bankers message instead of opening the accept flow.
    if (String(bondDetails?.countyId) === '55856') {
      setHarrisDialogOpen(true);
      return;
    }
    setSelectedQuote(quote);
    setAcceptModalOpen(true);
  };

  // Open decline modal
  const openDeclineModal = (quote) => {
    setSelectedQuote(quote);
    setDeclineModalOpen(true);
  };

  // Handle decline from within AcceptQuoteModal
  const handleDeclineFromAccept = () => {
    setAcceptModalOpen(false);
    setDeclineModalOpen(true);
  };

  // Can edit/cancel
  const canModify = bondDetails?.executingAgentID === null && bondDetails?.isDeferred === 'N';
  const quotes = bondDetails?.quotes || [];
  const sortedQuotes = useMemo(() => sortQuotes(quotes, sortBy), [quotes, sortBy]);
  const acceptedQuote = quotes.find((q) => q.transferBondQuoteID === bondDetails?.acceptedQuoteID);

  if (loading) {
    return (
      <OathPageWrapper maxWidth="max-w-7xl">
        <LoadingSkeleton />
      </OathPageWrapper>
    );
  }

  if (error && !bondDetails) {
    return (
      <OathPageWrapper maxWidth="max-w-7xl">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <p className="text-red-700 dark:text-red-400">{error}</p>
          <button
            onClick={() => navigate('/app/transfer-bonds?tab=requesting')}
            className="mt-2 text-sm text-red-600 dark:text-red-400 hover:underline"
          >
            Go back to list
          </button>
        </div>
      </OathPageWrapper>
    );
  }

  const defendantName = `${bondDetails?.defendantFirstName || ''} ${bondDetails?.defendantLastName || ''}`.trim() || 'Unknown';

  return (
    <OathPageWrapper maxWidth="max-w-7xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/app/transfer-bonds?tab=requesting')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">{defendantName}</h1>
            <StatusBadge
              status={bondDetails?.statusCd}
              hasBeenPosted={bondDetails?.hasBeenPosted}
              isDeferred={bondDetails?.isDeferred}
              executingAgentID={bondDetails?.executingAgentID}
            />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Reference #{bondDetails?.transferBondTransactionID}</p>
        </div>
        <button
          onClick={fetchDetails}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          title="Refresh"
        >
          <ArrowPathIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        </button>
      </div>

      {/* Error alert */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Action buttons */}
      {canModify && (
        <div className="flex gap-3 mb-6">
          <button
            onClick={() => setEditModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors"
          >
            <PencilIcon className="h-4 w-4" />
            Edit Request
          </button>
          <button
            onClick={() => setCancelModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <XCircleIcon className="h-4 w-4" />
            Cancel Request
          </button>
        </div>
      )}

      {/* Status Messages */}
      {bondDetails?.statusCd === 'C' && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Transaction Complete</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">This transaction is closed. No further action is required.</p>
        </div>
      )}

      {bondDetails?.hasBeenPosted && bondDetails?.statusCd !== 'C' && (
        <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
          <h3 className="font-semibold text-purple-900 dark:text-purple-300 mb-1">Pending Verification</h3>
          <p className="text-sm text-purple-700 dark:text-purple-400">
            The posting agent has uploaded the powers. A Bankers team member will verify the posting.
          </p>
        </div>
      )}

      {bondDetails?.isDeferred === 'Y' && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <h3 className="font-semibold text-red-900 dark:text-red-300 mb-1">Request Cancelled</h3>
          <p className="text-sm text-red-700 dark:text-red-400">This request has been cancelled. The information below is for reference only.</p>
        </div>
      )}

      {bondDetails?.executingAgentID && !bondDetails?.hasBeenPosted && bondDetails?.isDeferred === 'N' && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <UserIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
            <h3 className="font-semibold text-green-900 dark:text-green-300">Agent is Posting</h3>
          </div>
          {acceptedQuote && (
            <p className="text-sm text-green-700 dark:text-green-400">
              The bond should be posted by{' '}
              <span className="font-medium">
                {formatDateTime(
                  new Date(new Date(acceptedQuote.acceptedDate).getTime() + acceptedQuote.promisedPostingTimeInMinutes * 60 * 1000)
                )}
              </span>
            </p>
          )}
        </div>
      )}

      {/* Payment Required Alert */}
      {acceptedQuote?.paymentRequiredUpFront && (
        <div className="mb-6 p-4 bg-warning/10 border border-warning/30 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <ExclamationTriangleIcon className="h-5 w-5 text-warning" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Payment Required</h3>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Please reach out to a Bankers associate to process the payment before proceeding.
          </p>
        </div>
      )}

      {/* Quotes Section */}
      {!bondDetails?.executingAgentID && bondDetails?.isDeferred === 'N' && bondDetails?.statusCd !== 'C' && (
        <OathSectionCard
          title="Quotes"
          icon={DocumentTextIcon}
          className="mb-6"
          actions={quotes.length > 1 && (
            <QuoteSortControls sortBy={sortBy} onSortChange={setSortBy} />
          )}
        >
          {quotes.length === 0 ? (
            <div className="text-center py-8">
              <ClockIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">Waiting for quotes...</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">Quotes will appear here when agents respond.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedQuotes.map((quote) => (
                <QuoteCard
                  key={quote.transferBondQuoteID}
                  quote={quote}
                  isAccepted={quote.transferBondQuoteID === bondDetails?.acceptedQuoteID}
                  onView={() => {}}
                  onAccept={openAcceptModal}
                  onDecline={openDeclineModal}
                  canViewPostingAgency={canViewPostingAgency}
                  countdown={countdowns[quote.transferBondQuoteID] || formatTimeRemaining(quote.expireDate)}
                />
              ))}
            </div>
          )}
        </OathSectionCard>
      )}

      {/* Bond Details */}
      <OathSectionCard title="Transfer Bond Details" icon={InformationCircleIcon} className="mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4">
          <OathDetailField label="Reference #" value={bondDetails?.transferBondTransactionID} />
          <OathDetailField label="Total Bond Amount" value={formatCurrency(bondDetails?.totalBondAmount)} highlight />
          <OathDetailField label="Number of Bonds" value={bondDetails?.numberOfBonds} />
          <OathDetailField label="Created" value={formatDate(bondDetails?.createdAt)} />
        </div>
      </OathSectionCard>

      {/* Defendant Information */}
      <OathSectionCard title="Defendant Information" icon={UserIcon} className="mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4">
          <OathDetailField label="Name" value={defendantName} />
          <OathDetailField label="Date of Birth" value={formatDate(bondDetails?.defendantDateOfBirth)} />
          <OathDetailField label="SSN" value={formatSSN(bondDetails?.defendantSsn)} />
          <OathDetailField label="Phone" value={formatPhoneNumber(bondDetails?.defendantPhone)} />
          <OathDetailField label="Driver's License" value={bondDetails?.defendantDriversLicenseNumber} />
          <OathDetailField label="DL State" value={bondDetails?.driversLicenseState} />
          <OathDetailField label="Gender" value={bondDetails?.defendantGender} />
          <OathDetailField label="Race" value={bondDetails?.defendantRace} />
        </div>
      </OathSectionCard>

      {/* Location Information */}
      <OathSectionCard title="Location & Court" className="mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4">
          <OathDetailField label="County" value={bondDetails?.county} />
          <OathDetailField label="State" value={bondDetails?.state} />
          <OathDetailField label="Jail" value={bondDetails?.jailLocation} />
          <OathDetailField label="Jail Phone" value={formatPhoneNumber(bondDetails?.jailPhone)} />
          <OathDetailField label="Court" value={bondDetails?.courtPlace} />
          <OathDetailField label="Court Code" value={bondDetails?.courtCode} />
          <OathDetailField label="Booking Number" value={bondDetails?.bookingNumber} />
          <OathDetailField label="Arrest Date" value={formatDate(bondDetails?.arrestDate)} />
        </div>
      </OathSectionCard>

      {/* Extended Summary Fields - Physical description, identifiers, next of kin, etc. */}
      <ExtendedSummaryFields
        bondDetails={bondDetails}
        canViewPostingAgency={canViewPostingAgency}
        canViewRequestingAgency={canViewRequestingAgency}
      />

      {/* Fee Information */}
      {!!(bondDetails?.executionFee || bondDetails?.travelFee || bondDetails?.otherFees) && (
        <OathSectionCard title="Fee Information" className="mb-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4">
            <OathDetailField label="Execution Fee" value={formatCurrency(bondDetails?.executionFee)} />
            <OathDetailField label="Travel Fee" value={formatCurrency(bondDetails?.travelFee)} />
            <OathDetailField label="Other Fees" value={formatCurrency(bondDetails?.otherFees)} />
          </div>
        </OathSectionCard>
      )}

      {/* Bond Powers - Read-only display when posting agent is assigned */}
      <RequestPowersSection bondDetails={bondDetails} />

      {/* Uploaded Documents - Read-only, visible for all statuses including closed */}
      <RequestDocumentsSection bondDetails={bondDetails} />

      {/* Modals */}
      <CancelModal
        isOpen={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={handleCancelRequest}
        isSubmitting={isSubmitting}
      />

      <AcceptQuoteModal
        isOpen={acceptModalOpen}
        onClose={() => {
          setAcceptModalOpen(false);
          setSelectedQuote(null);
        }}
        onConfirm={handleAcceptQuote}
        onDecline={handleDeclineFromAccept}
        quote={selectedQuote}
        bondDetails={bondDetails}
        isSubmitting={isSubmitting}
      />

      <DeclineQuoteModal
        isOpen={declineModalOpen}
        onClose={() => {
          setDeclineModalOpen(false);
          setSelectedQuote(null);
        }}
        onConfirm={handleDeclineQuote}
        isSubmitting={isSubmitting}
      />

      <EditRequestModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSuccess={() => {
          setEditModalOpen(false);
          fetchDetails();
        }}
        bondDetails={bondDetails}
      />

      <OathModal
        isOpen={harrisDialogOpen}
        onClose={() => setHarrisDialogOpen(false)}
        title="Contact Bankers to Complete"
        size="md"
        footer={
          <div className="flex justify-end">
            <OathButton variant="primary" onClick={() => setHarrisDialogOpen(false)}>
              OK
            </OathButton>
          </div>
        }
      >
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 text-warning mt-0.5" />
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <p className="font-medium mb-2">
              Harris County, Texas transfer bonds cannot be completed through the portal.
            </p>
            <p>
              Please contact Bankers directly to finalize this transaction. Your request will
              remain open — you can cancel it from the request list if needed.
            </p>
          </div>
        </div>
      </OathModal>
    </OathPageWrapper>
  );
};

export default RequestDetailPage;
